import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { ArrowBack, OpenInNew, Share } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useScheme } from '../hooks/useSchemes';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  getLocalizedTitle, 
  getLocalizedDescription, 
  getLocalizedEligibility,
  getLocalizedProcedure,
  getLocalizedBenefits 
} from '../models/Scheme';

export const SchemeDetailPage: React.FC = () => {
  const { schemeId } = useParams<{ schemeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; url: string; label: string }>({ open: false, url: '', label: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { data: scheme, isLoading, error } = useScheme(schemeId || '');

  const handleShare = async () => {
    if (navigator.share && scheme) {
      try {
        await navigator.share({
          title: getLocalizedTitle(scheme, currentLanguage),
          text: getLocalizedDescription(scheme, currentLanguage),
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    }
  };

  const handleVisitPortal = () => {
    if (scheme?.officialLink) {
      setConfirmDialog({
        open: true,
        url: scheme.officialLink,
        label: t('official_website'),
      });
    }
  };

  const handleConfirmOpen = () => {
    const { url } = confirmDialog;
    setConfirmDialog({ open: false, url: '', label: '' });

    try {
      let finalUrl = url.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      setSnackbar({
        open: true,
        message: t('opening_external_site'),
        severity: 'success',
      });
    } catch (error) {
      console.error('Error opening URL:', error);
      setSnackbar({
        open: true,
        message: t('could_not_open_link'),
        severity: 'error',
      });
    }
  };

  const handleCancelOpen = () => {
    setConfirmDialog({ open: false, url: '', label: '' });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !scheme) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('error_occurred')}</Alert>
      </Container>
    );
  }

  const eligibility = getLocalizedEligibility(scheme, currentLanguage);
  const procedure = getLocalizedProcedure(scheme, currentLanguage);
  const benefits = getLocalizedBenefits(scheme, currentLanguage);

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h3" component="h1" sx={{ flexGrow: 1, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
          {getLocalizedTitle(scheme, currentLanguage)}
        </Typography>
        <IconButton onClick={handleShare}>
          <Share />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {scheme.isActive && (
          <Chip label={t('active')} color="success" />
        )}
        {scheme.isNew && (
          <Chip label={t('new')} color="primary" />
        )}
        {scheme.category && (
          <Chip label={scheme.category} variant="outlined" />
        )}
      </Box>

      <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}>
          {t('overview')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
          {getLocalizedDescription(scheme, currentLanguage)}
        </Typography>
      </Paper>

      {eligibility && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('eligibility')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
            {eligibility}
          </Typography>
        </Paper>
      )}

      {benefits && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('benefits')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
            {benefits}
          </Typography>
        </Paper>
      )}

      {procedure && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('application_procedure')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
            {procedure}
          </Typography>
        </Paper>
      )}

      {scheme.stateAvailability && scheme.stateAvailability.length > 0 && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('applicable_states')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {scheme.stateAvailability.map((state, index) => (
              <Chip key={index} label={state} size="small" />
            ))}
          </Box>
        </Paper>
      )}

      {scheme.launchDate && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, elevation: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('launch_date')}:</strong> {new Date(scheme.launchDate).toLocaleDateString()}
          </Typography>
        </Paper>
      )}

      {scheme.officialLink && (
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            bgcolor: '#E3F2FD',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(21, 101, 192, 0.3)',
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            {t('official_website')}
          </Typography>

          <Link
            href={scheme.officialLink}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: 'block',
              wordBreak: 'break-all',
              mb: 1,
              color: '#1565C0',
              fontWeight: 500,
            }}
            onClick={(e) => {
              e.preventDefault();
              handleVisitPortal();
            }}
          >
            {scheme.officialLink}
          </Link>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('verify_official_website')}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={<OpenInNew />}
            onClick={handleVisitPortal}
            sx={{
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('apply_official_website')}
          </Button>

          {scheme.lastUpdated && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>{t('details_updated_date')}:</strong> {new Date(scheme.lastUpdated).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <OpenInNew sx={{ color: '#1976D2', mr: 1.5 }} />
            <Typography variant="h6" component="span">
              {t('open_external_link')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('external_link_notice')}
          </Typography>
          <Paper
            sx={{
              p: 1.5,
              bgcolor: '#F5F5F5',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#1976D2',
                wordBreak: 'break-all',
                fontSize: '0.875rem',
              }}
            >
              {confirmDialog.url}
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            {t('external_link_continue')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelOpen} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmOpen}
            variant="contained"
            sx={{
              bgcolor: '#673AB7',
              '&:hover': { bgcolor: '#5E35B1' },
              textTransform: 'none',
            }}
          >
            {t('open_link')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  </>
  );
};
